import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import TripChatRoom from "@/components/chat/TripChatRoom";
import { normalizeSenderRole } from "@/components/chat/tripChatRoomUtils";
import { API_BASE_URL as API } from "@/apiConfig";

export default function TripChatPage() {
  const currentRole = "passenger";
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [language, setLanguage] = useState(searchParams.get("lang") || "en");
  const [chatData, setChatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [lostItemSubmitting, setLostItemSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notificationNotice, setNotificationNotice] = useState("");
  const latestIncomingMessageIdRef = useRef("");

  const updateIncomingMessageNotice = useCallback((nextChatData) => {
    const messages = nextChatData?.messages || [];
    const latestIncoming = [...messages].reverse().find((message) => (
      !message.deleted_at && normalizeSenderRole(message, nextChatData?.order) !== currentRole
    ));
    if (!latestIncoming?.id) return;
    if (latestIncomingMessageIdRef.current && latestIncomingMessageIdRef.current !== latestIncoming.id) {
      setNotificationNotice("New message received. Your typed text is kept.");
      document.title = "New message - Shonan Chat";
      if (navigator.vibrate) {
        navigator.vibrate(120);
      }
    }
    latestIncomingMessageIdRef.current = latestIncoming.id;
  }, []);

  const fetchTripChatMessages = useCallback(async (options = {}) => {
    if (!token) {
      throw new Error("Chat link is missing a token.");
    }
    const response = await axios.get(`${API}/trip/${orderId}/chat`, {
      params: {
        token,
        lang: language,
        limit: options.limit,
        before_message_id: options.beforeMessageId,
        before_created_at: options.beforeCreatedAt,
        after_message_id: options.afterMessageId,
        after_created_at: options.afterCreatedAt,
      },
    });
    updateIncomingMessageNotice(response.data);
    return response.data;
  }, [language, orderId, token, updateIncomingMessageNotice]);

  const fetchChat = useCallback(async () => {
    if (!token) {
      setError("Chat link is missing a token.");
      setLoading(false);
      return;
    }
    try {
      const data = await fetchTripChatMessages();
      setChatData(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "This chat link is invalid or no longer available.");
    } finally {
      setLoading(false);
    }
  }, [fetchTripChatMessages, token]);

  useEffect(() => {
    fetchChat();
  }, [fetchChat]);

  const handleSendMessage = async (body, sourceLanguage, attachments = []) => {
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/trip/${orderId}/chat/messages`,
        { body, source_language: sourceLanguage, attachments },
        { params: { token } },
      );
      return response.data;
    } finally {
      setSending(false);
    }
  };

  const handleSubmitFeedback = async (rating, comment) => {
    setFeedbackSubmitting(true);
    try {
      await axios.post(
        `${API}/trip/${orderId}/chat/feedback`,
        { rating, comment },
        { params: { token } },
      );
      await fetchChat();
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleSubmitLostItem = async (itemDescription, contactInfo) => {
    setLostItemSubmitting(true);
    try {
      await axios.post(
        `${API}/trip/${orderId}/chat/lost-item`,
        { item_description: itemDescription, contact_info: contactInfo },
        { params: { token } },
      );
      await fetchChat();
    } finally {
      setLostItemSubmitting(false);
    }
  };

  if (loading) {
    return <div className="trip-chat-luxury flex min-h-screen items-center justify-center bg-black p-6 text-[#f6d982]">Loading...</div>;
  }

  if (error) {
    return (
      <div className="trip-chat-luxury min-h-screen bg-black p-6">
        <div className="mx-auto max-w-xl rounded-2xl border border-[#d8b769]/25 bg-[#0c0a07]/90 p-4 text-[#f6d982]">
          {error}
        </div>
      </div>
    );
  }

  return (
    <TripChatRoom
      chatData={chatData}
      currentRole={currentRole}
      preferredLanguage={language}
      onPreferredLanguageChange={setLanguage}
      onSendMessage={handleSendMessage}
      onFetchMessages={fetchTripChatMessages}
      onSubmitFeedback={handleSubmitFeedback}
      feedbackSubmitting={feedbackSubmitting}
      onSubmitLostItem={handleSubmitLostItem}
      lostItemSubmitting={lostItemSubmitting}
      notificationNotice={notificationNotice}
      sending={sending}
    />
  );
}
